import classNames from 'clsx';
import { times, upperFirst } from 'lodash';
import React, { useMemo, useRef } from 'react';

import { LiveCasinoGameTile, NumberedGameTile, getIconClassKeys } from '@swf/casino';
import CasinoGameIcon from '@swf/components/CasinoGameIcon/CasinoGameIcon';
import type { Props as TileProps } from '@swf/components/CasinoGameTile/CasinoGameTile';
import CasinoGameTile from '@swf/components/CasinoGameTile/CasinoGameTile';
import Slider from '@swf/components/Slider/Slider';
import { MosaicContext } from '@swf/core/types/CasinoGame';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import type { CasinoGameList as CasinoGameListProps } from '@swf/core/types/CasinoGameList';
import type { CasinoMosaicLayout } from '@swf/generated/types/common/Casino';
import { CasinoIcon } from '@swf/generated/types/common/Casino';

import styles from './CasinoGameList.scss';

export type Props = CasinoGameListProps;

export const tilesPerRowFromIcon = (icon: CasinoIcon) => {
  switch (icon) {
    case CasinoIcon.icnHeroWide:
      return 1;
    case CasinoIcon.icnHero:
    case CasinoIcon.icnWide:
    case CasinoIcon.icnLive:
      return 3;
    default:
      return 6;
  }
};

const getMosiacProps = (mosaic: CasinoMosaicLayout) =>
  ['section1', 'section2', 'section3'].reduce<Partial<TileProps>[]>((memo, current) => {
    switch (mosaic[current]) {
      case '1 big square':
        memo.push({
          noIconScale: true,
          icon: CasinoIcon.icnSquare,
          className: styles.bigSquare,
          mosaicHint: MosaicContext.BigIcon
        });
        break;
      case '4 squares':
        times(4, () => memo.push({ icon: CasinoIcon.icnSquare }));
        break;
      case '1 top 2 bottom':
        memo.push({ icon: CasinoIcon.icnWide, className: styles.wide });
        times(2, () => memo.push({ icon: CasinoIcon.icnSquare }));
        break;
      case '2 top 1 bottom':
        memo.push({ icon: CasinoIcon.icnSquare });
        memo.push({ icon: CasinoIcon.icnWide, className: styles.wide });
        memo.push({ icon: CasinoIcon.icnSquare });
        break;
      case '1 top 1 bottom':
        times(2, () => memo.push({ icon: CasinoIcon.icnWide, className: styles.wide }));
        break;
    }
    return memo;
  }, []);

const isLive = (icon: CasinoIcon) => CasinoIcon.icnLive === icon;

const CasinoGameList: React.FC<Props> = ({
  placeholder,
  fsbLaunch,
  games,
  loading,
  onLaunch,
  onScroll,
  preload,
  shrink,
  sliderRef,
  race,
  mosaic,
  dynamicColumnFit = false,
  favouriteIcon,
  numbered,
  ...rest
}) => {
  const isMosaic = mosaic?.enabled;
  const slide = isMosaic || rest.slide;
  const icon = isMosaic ? CasinoIcon.icnSquare : rest.icon;
  const rows = isMosaic ? 2 : rest.rows;
  const tilesPerRow = rest.tilesPerRow || tilesPerRowFromIcon(icon);
  const innerRef = useRef<HTMLDivElement>(null);

  const mosaicProps = useMemo(
    () => isMosaic && getMosiacProps(mosaic as CasinoMosaicLayout),
    [isMosaic, mosaic]
  );

  const renderRows = isMosaic ? 2 : Math.min(rows || 0, Math.floor(games.length / tilesPerRow));

  const className = classNames(
    isMosaic && styles.mosaic,
    dynamicColumnFit && styles.dynamicGrid,
    dynamicColumnFit && styles[`dynamic${Math.min(6, games.length)}`],
    slide ? styles.slider : styles.grid,
    getIconClassKeys(icon).map((val: string) => styles[val]),
    numbered && styles.numbered,
    slide && styles[`rows${renderRows}`],
    shrink && styles[`cols${placeholder ? tilesPerRow : Math.min(tilesPerRow, games.length)}`],
    rest.className
  );

  let children = games.map((game, index) => {
    if (numbered) {
      return (
        <NumberedGameTile
          className={styles['fixedWidth' + upperFirst(icon)]}
          count={games.length}
          game={game as CasinoGame}
          race={race}
          icon={icon}
          index={index}
          fsbLaunch={fsbLaunch}
          key={index}
          onLaunch={onLaunch}
          preload={preload && index < tilesPerRow * (slide && !isMosaic ? 1 : 3)}
          favouriteIcon={favouriteIcon}
        />
      );
    } else {
      return isLive(icon) ? (
        <LiveCasinoGameTile
          key={index}
          game={game}
          grid={!slide}
          onLaunch={onLaunch}
          favouriteIcon={favouriteIcon}
        />
      ) : (
        <CasinoGameTile
          count={games.length}
          game={game as CasinoGame}
          race={race}
          icon={icon}
          index={index}
          fsbLaunch={fsbLaunch}
          key={index}
          onLaunch={onLaunch}
          preload={preload && index < tilesPerRow * (slide && !isMosaic ? 1 : 3)}
          favouriteIcon={favouriteIcon}
          liveRaceIcon={false}
          {...(mosaicProps && mosaicProps[index % mosaicProps.length])}
        />
      );
    }
  });

  if ((!isMosaic && !games.length) || loading) {
    children = children.concat(
      times((games.length % tilesPerRow) + tilesPerRow * (rows || 1), (count) => (
        <CasinoGameIcon key={games.length + count} />
      ))
    );
  } else if (!isMosaic && placeholder && 'icnSquare' === icon) {
    children = children.concat(
      times(tilesPerRow - games.length, (key) =>
        React.cloneElement(placeholder, { key: games.length + key })
      )
    );
  }

  if (slide) {
    return (
      <Slider
        innerClassName={classNames(className, { [styles.liveCasino]: isLive(icon) })}
        onScroll={onScroll}
        ref={sliderRef}
        innerRef={innerRef}
      >
        {children}
      </Slider>
    );
  }

  return (
    <div className={classNames(className, { [styles.liveCasino]: isLive(icon) })}>{children}</div>
  );
};

export default CasinoGameList;
